import { createResource, createSignal, createMemo, For, Show } from 'solid-js';

// Kontrak — contract roster: every active CONTRACT (status=Kontrak) employee of
// the company with their contract window and "sisa kontrak" (days remaining,
// computed live from the end date). Filter by department, search by name/NIK,
// sort any column by clicking its header, and renew a contract in place.

async function getJSON(url) {
  const res = await fetch(url);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || ('HTTP ' + res.status));
  return data;
}
async function postJSON(url, body) {
  const res = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
  const data = await res.json().catch(() => ({}));
  return { ok: res.ok, data };
}

// ── date helpers ──────────────────────────────────────────────────────────
const fmtISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const todayISO = () => fmtISO(new Date());
function addDaysISO(iso, days) { const d = new Date(iso + 'T00:00:00'); d.setDate(d.getDate() + days); return fmtISO(d); }
function addYearsISO(iso, years) { const d = new Date(iso + 'T00:00:00'); d.setFullYear(d.getFullYear() + years); return fmtISO(d); }
// whole days from today to an ISO date (negative = past); null on bad/empty.
function daysUntil(iso) {
  if (!iso) return null;
  const d = new Date(iso + 'T00:00:00');
  if (isNaN(d)) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return Math.round((d - today) / 86400000);
}

// SisaCell — the remaining-days cell, colour-coded like the Karyawan contract badge.
function SisaCell(props) {
  const n = () => props.days;
  return (
    <Show when={n() !== null} fallback={<span class="vg-dim">—</span>}>
      <Show when={n() < 0} fallback={
        <span class={`badge ${n() <= 30 ? 'warning' : 'success'}`}>
          {n() === 0 ? 'berakhir hari ini' : `${n()} hari`}
        </span>
      }>
        <span class="badge danger">kadaluarsa {Math.abs(n())} hari</span>
      </Show>
    </Show>
  );
}

export default function Kontrak() {
  const [list, { refetch }] = createResource(() => getJSON('/api/contracts'));
  const [dept, setDept] = createSignal('');
  const [q, setQ] = createSignal('');
  const [sort, setSort] = createSignal({ col: 'sisa', dir: 'asc' }); // soonest-expiring first
  const [renew, setRenew] = createSignal(null); // { nik, name, start, end }
  const [msg, setMsg] = createSignal(null);
  const [busy, setBusy] = createSignal(false);

  const departments = createMemo(() => {
    const s = new Set();
    for (const r of list() ?? []) if (r.department) s.add(r.department);
    return [...s].sort();
  });

  const rows = createMemo(() => {
    const d = dept(), kw = q().trim().toLowerCase();
    const arr = (list() ?? [])
      .filter((r) => (!d || r.department === d) &&
        (!kw || (r.name || '').toLowerCase().includes(kw) || (r.nik || '').toLowerCase().includes(kw)))
      .map((r) => ({ ...r, sisa: daysUntil(r.contractEnd) }));
    const { col, dir } = sort();
    const mul = dir === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      switch (col) {
        case 'name': return (a.name || '').localeCompare(b.name || '') * mul;
        case 'department': return (a.department || '').localeCompare(b.department || '') * mul;
        case 'start': return (a.contractStart || '').localeCompare(b.contractStart || '') * mul;
        case 'end': return (a.contractEnd || '').localeCompare(b.contractEnd || '') * mul;
        case 'sisa':
        default:
          // employees with no end date sort last regardless of direction
          if (a.sisa === null && b.sisa === null) return 0;
          if (a.sisa === null) return 1;
          if (b.sisa === null) return -1;
          return (a.sisa - b.sisa) * mul;
      }
    });
    return arr;
  });

  function toggleSort(col) {
    const s = sort();
    setSort(s.col === col ? { col, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'asc' });
  }
  const arrow = (col) => (sort().col === col ? (sort().dir === 'asc' ? ' ▲' : ' ▼') : '');

  function openRenew(r) {
    const start = r.contractEnd ? addDaysISO(r.contractEnd, 1) : todayISO();
    setRenew({ nik: r.nik, name: r.name, start, end: addYearsISO(start, 1) });
  }
  async function saveRenew() {
    const v = renew();
    if (!v.start || !v.end) { setMsg({ ok: false, t: 'Tanggal mulai dan akhir wajib diisi.' }); return; }
    setBusy(true);
    const res = await postJSON(`/api/employee/${encodeURIComponent(v.nik)}/contract`,
      { contractStart: v.start, contractEnd: v.end });
    setBusy(false);
    if (res.ok) { setMsg({ ok: true, t: `Kontrak ${v.name} diperbarui hingga ${v.end}.` }); setRenew(null); refetch(); }
    else setMsg({ ok: false, t: res.data.error });
  }

  return (
    <>
      <div class="page-head">
        <h2>Kontrak</h2>
        <div class="spacer" />
        <Show when={list()}>
          <span class="kg-meta-text">{rows().length} / {list().length} karyawan kontrak</span>
        </Show>
      </div>

      <Show when={msg()}>
        <div class={`kg-verdict ${msg().ok ? 'ok' : 'fail'}`} style="margin-bottom:12px">{msg().t}</div>
      </Show>

      <div class="card card-pad">
        <p class="ky-vars-note">
          Karyawan berstatus <b>Kontrak</b> di perusahaan ini beserta sisa masa kontraknya. Klik
          judul kolom untuk mengurutkan; gunakan <b>Perbarui</b> untuk memperpanjang kontrak.
        </p>

        <div class="lb-filter">
          <label class="kg-field"><span>Departemen</span>
            <select class="kg-select" value={dept()} onInput={(e) => setDept(e.currentTarget.value)}>
              <option value="">Semua departemen</option>
              <For each={departments()}>{(d) => <option value={d}>{d}</option>}</For>
            </select>
          </label>
          <label class="kg-field lb-search"><span>Cari nama / NIK</span>
            <input type="text" placeholder="kata kunci…" value={q()} onInput={(e) => setQ(e.currentTarget.value)} />
          </label>
          <Show when={dept() || q()}>
            <button class="btn ghost" onClick={() => { setDept(''); setQ(''); }}>Reset</button>
          </Show>
        </div>

        <Show when={list.error}>
          <div class="kg-verdict fail">{String(list.error.message || list.error)}</div>
        </Show>

        <div class="tbl-scroll">
          <table class="data kontrak-tbl">
            <thead>
              <tr>
                <th class="sortable" onClick={() => toggleSort('name')}>Karyawan{arrow('name')}</th>
                <th class="sortable" onClick={() => toggleSort('department')}>Departemen{arrow('department')}</th>
                <th class="sortable" onClick={() => toggleSort('start')}>Mulai{arrow('start')}</th>
                <th class="sortable" onClick={() => toggleSort('end')}>Berakhir{arrow('end')}</th>
                <th class="sortable num" onClick={() => toggleSort('sisa')}>Sisa kontrak{arrow('sisa')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <For each={rows()}>{(r) =>
                <>
                  <tr classList={{ selected: renew()?.nik === r.nik }}>
                    <td><div class="kg-code">{r.nik}</div><div class="kg-sub">{r.name}</div></td>
                    <td>{r.department || <span class="vg-dim">—</span>}</td>
                    <td class="ky-nik">{r.contractStart || <span class="vg-dim">—</span>}</td>
                    <td class="ky-nik">{r.contractEnd || <span class="vg-dim">—</span>}</td>
                    <td class="num"><SisaCell days={r.sisa} /></td>
                    <td class="num">
                      <button class="btn ghost" onClick={() => (renew()?.nik === r.nik ? setRenew(null) : openRenew(r))}>
                        {renew()?.nik === r.nik ? 'Tutup' : 'Perbarui'}
                      </button>
                    </td>
                  </tr>
                  <Show when={renew()?.nik === r.nik}>
                    <tr class="kontrak-renew-row">
                      <td colspan="6">
                        <div class="kontrak-renew-grid">
                          <label class="kg-field"><span>Mulai kontrak</span>
                            <input type="date" value={renew().start} onInput={(e) => setRenew({ ...renew(), start: e.currentTarget.value })} /></label>
                          <label class="kg-field"><span>Akhir kontrak</span>
                            <input type="date" value={renew().end} onInput={(e) => setRenew({ ...renew(), end: e.currentTarget.value })} /></label>
                          <button class="btn primary" disabled={busy()} onClick={saveRenew}>{busy() ? 'Menyimpan…' : 'Simpan'}</button>
                          <button class="btn ghost" disabled={busy()} onClick={() => setRenew(null)}>Batal</button>
                        </div>
                      </td>
                    </tr>
                  </Show>
                </>
              }</For>
              <Show when={list() && rows().length === 0}>
                <tr><td colspan="6" class="kg-meta-text">Tidak ada karyawan kontrak{dept() || q() ? ' yang cocok dengan filter' : ''}.</td></tr>
              </Show>
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
